import * as React from 'react';
import {useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {FormShell} from 'components/form/FormShell';
import {OptionType, Select} from 'components/form/Select';
import {TextInput} from 'components/form/TextInput';
import {usePrompt} from 'helpers/useBlocker';
import {observer} from 'mobx-react-lite';
import {appState} from 'store/AppStore';
import {VehicleState} from 'store/vehicle/VehicleState';
import {CarServiceDto} from '../../../../../../PRMGApiClient';
import {VehicleEditKey} from '../VehicleEditView';

interface VehicleServiceDataFormProps {
  state: VehicleState;
}

export const VehicleServiceDataForm: React.FC<VehicleServiceDataFormProps> = observer(({state}): JSX.Element => {
  const {t} = useTranslation();

  const {vehicleId} = useParams();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: {errors, isDirty},
  } = useForm<CarServiceDto>({
    mode: 'onTouched',
    defaultValues: {
      bodyType: undefined,
      fuel: undefined,
      engineOil: null,
      drive: undefined,
      gear: undefined,
      bodyColorId: null,
      bodyColorMakeCode: null,
      paintEffect: undefined,
      paintProtection: undefined,
    },
  });

  useEffect(() => {
    if (vehicleId) reset(state.item.service);
  }, [state.item, vehicleId, reset]);

  useEffect(() => {
    state.setDirty(VehicleEditKey.serviceData, isDirty);
  }, [isDirty]);

  usePrompt(t('Are you sure? There are unsaved changes.'), isDirty);

  return (
    <FormShell
      isDirty={isDirty}
      onReset={() => reset()}
      onSubmit={handleSubmit((value) => state.updateService(value))}
      isLoading={state.isLoading}
    >
      <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-2">
        <Controller
          name="bodyType"
          control={control}
          render={({field: {onChange, value}}) => (
            <Select
              id="bodyType"
              label={t('Body type')}
              options={appState.bodyTypeOptions}
              value={appState.bodyTypeOptions?.find((item) => item.value === value) ?? null}
              error={errors.bodyType}
              onChange={(e) => onChange((e as OptionType).value)}
            />
          )}
        />
        <Controller
          name="fuel"
          control={control}
          render={({field: {onChange, value}}) => (
            <Select
              id="fuel"
              label={t('Fuel type')}
              options={appState.fuelOptions}
              value={appState.fuelOptions?.find((item) => item.value === value) ?? null}
              error={errors.fuel}
              onChange={(e) => onChange((e as OptionType).value)}
            />
          )}
        />
        <TextInput label={t('Engine oil')} {...register('engineOil')} error={errors.engineOil} />
        <Controller
          name="drive"
          control={control}
          render={({field: {onChange, value}}) => (
            <Select
              id="drive"
              label={t('Drive type')}
              options={appState.driveOptions}
              value={appState.driveOptions?.find((item) => item.value === value) ?? null}
              error={errors.drive}
              onChange={(e) => onChange((e as OptionType).value)}
            />
          )}
        />
        <Controller
          name="gear"
          control={control}
          render={({field: {onChange, value}}) => (
            <Select
              id="gear"
              label={t('Transmission type')}
              options={appState.gearOptions}
              value={appState.gearOptions?.find((item) => item.value === value) ?? null}
              error={errors.gear}
              onChange={(e) => onChange((e as OptionType).value)}
            />
          )}
        />
        <Controller
          name="bodyColorId"
          control={control}
          render={({field: {onChange, value}}) => (
            <Select
              id="bodyColorId"
              label={t('Paint color')}
              options={appState.colorOptions}
              value={appState.colorOptions?.find((item) => item.value === value) ?? null}
              error={errors.bodyColorId}
              onChange={(e) => onChange((e as OptionType).value)}
            />
          )}
        />
        <TextInput label={t('Paint code')} {...register('bodyColorMakeCode')} error={errors.engineOil} />
        <Controller
          name="paintEffect"
          control={control}
          render={({field: {onChange, value}}) => (
            <Select
              id="paintEffect"
              label={t('Paint Type')}
              options={appState.paintEffectOptions}
              value={appState.paintEffectOptions?.find((item) => item.value === value) ?? null}
              error={errors.paintEffect}
              onChange={(e) => onChange((e as OptionType).value)}
            />
          )}
        />
        <Controller
          name="paintProtection"
          control={control}
          render={({field: {onChange, value}}) => (
            <Select
              id="paintProtection"
              label={t('Paint protection')}
              options={appState.paintProtectionOptions}
              value={appState.paintProtectionOptions?.find((item) => item.value === value) ?? null}
              error={errors.paintProtection}
              onChange={(e) => onChange((e as OptionType).value)}
            />
          )}
        />
      </div>
    </FormShell>
  );
});
